import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Card,
  CardContent,
  Grid,
  Divider,
  Container,
  CircularProgress,
  Button,
  Slide,
} from "@mui/material";
import {
  Info as InfoIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import { GET_TRAINING_MODULE_BY_ID } from "../../../apis/training-apis/trainingApis";
import { useQuery } from "@apollo/client";
import TrainingMaterialCard from "../TrainingComponents/TrainingMaterialCard";
import TrainingMaterialListCard from "../TrainingComponents/TrainingMaterialListCard";
import { ToggleButtonGroup, ToggleButton } from "@mui/material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import { getIconByType } from "../utils/TrainingUtils";
import { TrainingModuleStatusKey, ViewType } from "../utils/enums";
import { useTheme } from "@mui/material/styles";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";

export default function TrainingOverviewTab({
  training,
  onStatusChange,
  onClose,
  refetch,
  onOpenMaterial,
  trainingModuleStatusMap,
  trainingSectionMap,
  setSnackbarInfo,
}) {
  const [viewType, setViewType] = React.useState(ViewType.GRID);
  const [updating, setUpdating] = useState(false);
  const [activating, setActivating] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [isDeactivated, setIsDeactivated] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [dialogContent, setDialogContent] = useState({
    heading: "",
    content: "",
    buttonText: "",
  });
  const theme = useTheme();

  const TransitionComponent = (props) => {
    return <Slide {...props} direction="left" />;
  };

  const handleUpdateStatus = async (statusId) => {
    setUpdating(true);
    if (statusId === trainingModuleStatusMap[TrainingModuleStatusKey.ACTIVE]) {
      setActivating(true);
    }
    try {
      const success = await onStatusChange(training?.id, statusId);
      if (success) {
        await refetch();
        if (
          statusId === trainingModuleStatusMap[TrainingModuleStatusKey.ACTIVE]
        ) {
          setIsActivated(true);
          setSnackbarInfo({
            message: success
              ? "Training course activated successfully!"
              : "Failed to activate training course!",
            vertical: "top",
            horizontal: "right",
            Transition: TransitionComponent,
          });
        } else {
          setIsDeactivated(true);
          setSnackbarInfo({
            message: success
              ? "Training course deactivated successfully!"
              : "Failed to deactivate training course!",
            vertical: "top",
            horizontal: "right",
            Transition: TransitionComponent,
          });
        }

        // onClose(); // Close only if success
      }
    } catch {
      console.error("Error in updating the course status");
    } finally {
      setUpdating(false);
      setActivating(false);
    }
  };

  const handleConfirmAction = (action, statusId) => {
    const content = {
      heading:
        action === "activate"
          ? "Activate Training Course"
          : "Deactivate Training Course",
      content:
        action === "activate"
          ? "Are you sure you want to activate this training course? Once you activate this training it will be assigned to all users."
          : "Are you sure you want to deactivate this training course? This will remove it from all users. Once deactivated, it cannot be activated again.",
      buttonText: action === "activate" ? "Activate" : "Deactivate",
    };

    setDialogContent(content);
    setConfirmAction({ action, statusId });
    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    if (confirmAction) {
      await handleUpdateStatus(confirmAction.statusId);
      setShowConfirmDialog(false);
      // Don't clear confirmAction immediately to prevent content flicker
      setTimeout(() => {
        setConfirmAction(null);
      }, 300); // Wait for dialog close animation
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
    // Don't clear confirmAction immediately to prevent content flicker
    setTimeout(() => {
      setConfirmAction(null);
    }, 300); // Wait for dialog close animation
  };

  const handleViewChange = (_, nextView) => {
    if (nextView !== null && Object.values(ViewType).includes(nextView)) {
      setViewType(nextView);
    }
  };

  const { data, loading } = useQuery(GET_TRAINING_MODULE_BY_ID, {
    variables: { id: training?.id },
  });

  return (
    <Box sx={{ p: 2, backgroundColor: theme.customColors.background.header }}>
      <Grid container spacing={2}>
        {/* Other Details Card */}
        <Grid item xs={12} md={3}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
              transition: "box-shadow 0.3s ease",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <InfoIcon
                  sx={{ color: theme.customColors.icons.primary, fontSize: 18 }}
                />
                <Typography
                  variant="h5"
                  fontWeight={600}
                  sx={{ color: theme.customColors.text.strong }}
                >
                  Other Details
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      backgroundColor: theme.customColors.background.tableRow,
                      color: theme.customColors.text.secondary,
                      width: 28,
                      height: 28,
                    }}
                  >
                    <CategoryIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "10px",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Category
                    </Typography>
                    <Chip
                      label={training?.category ?? "N/A"}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        borderColor: theme.customColors.border.disabled,
                        color: theme.customColors.text.strong,
                        mt: 0.5,
                        alignSelf: "flex-start",
                        height: "20px",
                      }}
                    />
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      backgroundColor: theme.customColors.background.tableRow,
                      color: theme.customColors.text.secondary,
                      width: 28,
                      height: 28,
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "10px",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Created At
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ color: theme.customColors.text.strong }}
                    >
                      {training?.created_at
                        ? new Date(
                          Number(training?.created_at)
                        ).toLocaleString()
                        : "Not Available"}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      backgroundColor: theme.customColors.background.tableRow,
                      color: theme.customColors.text.secondary,
                      width: 28,
                      height: 28,
                    }}
                  >
                    <LocalLibraryIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "10px",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Source
                    </Typography>
                    <Chip
                      //   label={training?.isImported ? "Library" : "Manual"}
                      label="Library"
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        borderColor: training?.isImported
                          ? theme.customColors.icons.primary
                          : theme.customColors.status.success.main,
                        color: training?.isImported
                          ? theme.customColors.icons.primary
                          : theme.customColors.status.success.main,
                        backgroundColor: training?.isImported
                          ? theme.customColors.status.info.bg
                          : theme.customColors.status.success.bg,
                        mt: 0.5,
                        alignSelf: "flex-start",
                        height: "20px",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Placeholder Card for Training Analysis Section */}
        <Grid item xs={12} md={9}>
          <Card
            sx={{
              height: "450px", // or any fixed height you want
              display: "flex",
              overflow: "auto",
              flexDirection: "column", // stack children vertically
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
              transition: "box-shadow 0.3s ease",
            }}
          >
            {loading ? (
              <Container
                component="main"
                maxWidth="xs"
                sx={{ textAlign: "center", mt: 10 }}
              >
                <CircularProgress />
              </Container>
            ) : (
              (() => {
                const sections =
                  data?.getTrainingModuleById?.data?.sections ?? [];
                return (
                  <>
                    <CardContent sx={{ p: 2 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0}
                      >
                        <Typography variant="h5" fontWeight="bold">
                          Training Material ({sections?.length})
                        </Typography>
                        <ToggleButtonGroup
                          value={viewType}
                          exclusive
                          onChange={handleViewChange}
                          size="small"
                        >
                          <ToggleButton
                            value={ViewType.GRID}
                            aria-label="grid view"
                          >
                            <ViewModuleIcon />
                          </ToggleButton>
                          <ToggleButton
                            value={ViewType.LIST}
                            aria-label="list view"
                          >
                            <ViewListIcon />
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      <Grid container mt={0} spacing={3}>
                        {sections?.map((section) => (
                          <Grid
                            item
                            xs={12}
                            sm={viewType === ViewType.GRID ? 6 : 6}
                            md={viewType === ViewType.GRID ? 4 : 6}
                            lg={viewType === ViewType.GRID ? 3 : 6}
                            key={section?.id}
                          >
                            {viewType === ViewType.GRID ? (
                              <TrainingMaterialCard
                                section={section}
                                getIconByType={getIconByType}
                                onClick={onOpenMaterial}
                                small={true}
                                showCompletetionStaus={false}
                                trainingSectionMap={trainingSectionMap}
                              />
                            ) : (
                              <TrainingMaterialListCard
                                section={section}
                                getIconByType={getIconByType}
                                onClick={onOpenMaterial}
                                small={true}
                                showCompletetionStaus={false}
                                trainingSectionMap={trainingSectionMap}
                              />
                            )}
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </>
                );
              })()
            )}
          </Card>
        </Grid>
      </Grid>
      {/* Actions Section */}
      {training?.status_key !== TrainingModuleStatusKey.INACTIVE && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
            alignItems: "center",
            px: 3,
            py: 2,
            backgroundColor: theme.customColors.background.main,
            borderTop: "1px solid",
            borderColor: theme.customColors.border.strong,
            boxShadow: "0 -1px 3px rgba(0,0,0,0.05)",
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: "1px",
              background: `linear-gradient(90deg, transparent 0%, ${theme.customColors.border.subtle} 50%, transparent 100%)`,
              mr: 2,
            }}
          />

          {training?.status_key === TrainingModuleStatusKey.DRAFT ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                Once you activate this training it will be assigned to all
                users.
              </Typography>

              <Button
                onClick={() =>
                  handleConfirmAction(
                    "deactivate",
                    trainingModuleStatusMap[TrainingModuleStatusKey.INACTIVE]
                  )
                }
                variant="outlined"
                disabled={isDeactivated}
                startIcon={<UnpublishedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "none",
                  minWidth: "140px",
                  borderWidth: "2px",
                  transition: "all 0.2s ease",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  De-Activate
                </Box>
              </Button>

              <Button
                variant="contained"
                disabled={isActivated || isDeactivated}
                startIcon={<PublishedWithChangesIcon sx={{ fontSize: 16 }} />}
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "none",
                  minWidth: "140px",
                }}
                onClick={() =>
                  handleConfirmAction(
                    "activate",
                    trainingModuleStatusMap[TrainingModuleStatusKey.ACTIVE]
                  )
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  Activate
                </Box>
              </Button>
            </Box>
          ) : training?.status_key === TrainingModuleStatusKey.ACTIVE ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                Deactivate this training to stop it from being accessed by
                users.
              </Typography>
              <Button
                onClick={() =>
                  handleConfirmAction(
                    "deactivate",
                    trainingModuleStatusMap[TrainingModuleStatusKey.INACTIVE]
                  )
                }
                variant="outlined"
                disabled={isDeactivated}
                startIcon={<UnpublishedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "none",
                  minWidth: "140px",
                  borderWidth: "2px",
                  transition: "all 0.2s ease",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  De-Activate
                </Box>
              </Button>
            </Box>
          ) : null}
        </Box>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={showConfirmDialog}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirm}
        heading={dialogContent.heading}
        content={dialogContent.content}
        buttonText={dialogContent.buttonText}
        loading={updating}
      />
    </Box>
  );
}
